/**
 * lightsFromState(bundle, diagState): what is lit, pure (plan sections 4, 5 and 12).
 *
 * bundle = GET /api/v5/brain/category (category_bundle); diagState = the latest tse:diag_state of the chat (or null).
 * Returns {nodes: Map<nid,{nid, role, label, tone, lit, pct, status, observed}>,
 *          edges: [{id, src, dst, kind, traversed, lead, alpha, width, seen, wins}]   (every bundle edge; traversed ones carry colour),
 *          edgeById: Map<id, edge>, adjacency: Map<nid, Set<nid>>, focus: [nid], stage, clusterMass: Map<lod id, 0..1>,
 *          summary: {causes, lit, ruledOut, observed, leading: [{nid, label, pct, status}], ruled: [{nid, label}]}}.
 */
import {
  CAUSE_STATUS, EDGE, EDGE_KINDS, ENDED_LIFECYCLE, LIKELY_PHASES, LIT_EXPONENT, LIT_MIN_CANDIDATE, MAX_LIVE_CAUSES, OBSERVED_LIT,
  OBSERVED_VALUE, ROLE_OF_TYPE, ROLES, STAGES, TONE_LIT, TONES,
} from "./lightsConstants";

const LIKELY_OTHERS_SCALE = 0.35;   // in the likely-cause phase the other candidates hold just above ember level
const PERCENT_MAX = 100;

const list = (v) => (Array.isArray(v) ? v : []);
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const causeLit = (pct) => clamp01((Number(pct) || 0) / PERCENT_MAX) ** LIT_EXPONENT;

function baseNodes(bundle) {
  const causeLabels = new Map(list(bundle?.causes).map((c) => [c.cause_key, c.label || c.title]));
  const nodes = new Map();
  list(bundle?.graph?.nodes).forEach((n) => {
    const role = n && n.nid && ROLE_OF_TYPE[n.type];
    if (!role) return;
    const label = (role === ROLES.CAUSE && causeLabels.get(n.nid)) || n.label || n.key || n.nid;
    nodes.set(n.nid, { nid: n.nid, role, label, tone: TONES.DIM, lit: TONE_LIT[TONES.DIM], pct: null, status: null, observed: null });
  });
  return nodes;
}

function toneOfCause(status, pct) {
  switch (status) {
    case CAUSE_STATUS.LEADING:
    case CAUSE_STATUS.CONFIRMED:
      return { tone: TONES.LEADING, lit: TONE_LIT[TONES.LEADING] };
    case CAUSE_STATUS.RULED_OUT:
    case CAUSE_STATUS.FAILED:
      return { tone: TONES.EMBER, lit: TONE_LIT[TONES.EMBER] };
    case CAUSE_STATUS.EVALUATING:
      return { tone: TONES.EVALUATING, lit: Math.max(causeLit(pct), OBSERVED_LIT.unknown) };
    default:
      return { tone: TONES.LIT, lit: causeLit(pct) };
  }
}

function applyCauses(nodes, diag) {
  list(diag.causes).forEach((c) => {
    const node = nodes.get(c.key);
    if (!node) return;
    Object.assign(node, toneOfCause(c.status, c.percentage), { pct: Number(c.percentage) || 0, status: c.status || null });
    if (c.name) node.label = c.name;
  });
  list(diag.ruled_out).forEach((r) => {
    const node = nodes.get(r.cause_key);
    if (node && node.tone !== TONES.LEADING) Object.assign(node, { tone: TONES.EMBER, lit: TONE_LIT[TONES.EMBER], status: CAUSE_STATUS.RULED_OUT });
  });
}

function applyObserved(nodes, diag) {
  list(diag.observed).forEach((o) => {
    const node = nodes.get(o.id);
    if (!node || node.role === ROLES.CAUSE) return;
    node.observed = { value: o.value || OBSERVED_VALUE.UNKNOWN, source: o.source || null };
    if (o.label) node.label = o.label;
    if (o.value === OBSERVED_VALUE.YES) Object.assign(node, { tone: TONES.LIT, lit: OBSERVED_LIT.yes });
    else if (o.value === OBSERVED_VALUE.NO) Object.assign(node, { tone: TONES.DENIED, lit: TONE_LIT[TONES.DENIED] });
    else Object.assign(node, { tone: TONES.LIT, lit: OBSERVED_LIT.unknown });
  });
}

/** A fact nobody observed whose every supported cause is an ember fades out: that street is closed. */
function fadeDeadFacts(nodes, edges) {
  const targets = new Map();
  edges.forEach((e) => {
    if (e.type !== EDGE_KINDS.SUPPORTS) return;
    if (!targets.has(e.src)) targets.set(e.src, []);
    targets.get(e.src).push(e.dst);
  });
  targets.forEach((causes, src) => {
    const node = nodes.get(src);
    if (!node || node.tone !== TONES.DIM) return;
    const known = causes.map((c) => nodes.get(c)).filter(Boolean);
    if (known.length && known.every((c) => c.tone === TONES.EMBER)) Object.assign(node, { tone: TONES.FADED, lit: TONE_LIT[TONES.FADED] });
  });
}

function stageOf(diag, nodes) {
  if (!diag || !list(diag.causes).length) return STAGES.CATEGORY;
  if (diag.lifecycle === ENDED_LIFECYCLE) return STAGES.ENDED;
  const confirmed = list(diag.causes).some((c) => c.status === CAUSE_STATUS.CONFIRMED);
  if (confirmed || LIKELY_PHASES.includes(diag.phase)) return STAGES.LIKELY;
  const narrowed = list(diag.ruled_out).length > 0 || [...nodes.values()].some((n) => n.tone === TONES.EMBER);
  return narrowed || diag.last ? STAGES.NARROWING : STAGES.CANDIDATES;
}

const isObservedLit = (n) => n.role !== ROLES.CAUSE && n.tone === TONES.LIT && n.lit >= OBSERVED_LIT.yes;
export const isCandidate = (n) => n.role === ROLES.CAUSE && n.tone !== TONES.EMBER && n.lit >= LIT_MIN_CANDIDATE;

function focusOf(stage, nodes, diag) {
  if (stage === STAGES.CATEGORY || stage === STAGES.ENDED) return [];
  const all = [...nodes.values()];
  if (stage !== STAGES.LIKELY) return all.filter((n) => isObservedLit(n) || isCandidate(n)).map((n) => n.nid);
  const leaders = all.filter((n) => n.tone === TONES.LEADING);
  const clueIds = new Set(list(diag.causes).filter((c) => nodes.get(c.key)?.tone === TONES.LEADING)
    .flatMap((c) => list(c.clues).map((k) => k.fact_id)));
  const clues = all.filter((n) => clueIds.has(n.nid) && isObservedLit(n));
  return [...leaders, ...clues].map((n) => n.nid);
}

function dimOthersWhenLikely(stage, nodes) {
  if (stage !== STAGES.LIKELY) return;
  nodes.forEach((n) => {
    if (n.role === ROLES.CAUSE && (n.tone === TONES.LIT || n.tone === TONES.EVALUATING)) n.lit *= LIKELY_OTHERS_SCALE;
  });
}

export const edgeId = (src, dst, kind) => `${src}>${dst}:${kind}`;

/** Every bundle edge, faint by default; a supports edge with both ends lit is traversed; a rules_out edge is traversed when it ruled. */
function allEdges(nodes, edges, diag) {
  const ruledBy = new Set(list(diag?.ruled_out).filter((r) => r.by_fact).map((r) => `${r.by_fact}>${r.cause_key}`));
  const out = [];
  edges.forEach((e) => {
    const a = nodes.get(e.src);
    const b = nodes.get(e.dst);
    if (!a || !b) return;
    const kind = e.type === EDGE_KINDS.RULES_OUT ? EDGE_KINDS.RULES_OUT : EDGE_KINDS.SUPPORTS;
    const seen = Number(e.seen) || 0;
    const share = seen > 0 ? clamp01((Number(e.wins) || 0) / seen) : 0;
    const edge = {
      id: edgeId(e.src, e.dst, kind), src: e.src, dst: e.dst, kind, traversed: false, lead: false, alpha: 0,
      width: EDGE.MIN_WIDTH + EDGE.WIN_WIDTH * share, seen, wins: Number(e.wins) || 0,
    };
    if (kind === EDGE_KINDS.RULES_OUT) {
      if (ruledBy.has(`${e.src}>${e.dst}`)) Object.assign(edge, { traversed: true, alpha: EDGE.RULES_OUT_ALPHA, width: EDGE.MIN_WIDTH });
    } else if (diag && a.tone !== TONES.EMBER && b.tone !== TONES.EMBER) {
      const lit = Math.min(a.lit, b.lit);
      if (lit >= EDGE.MIN_LIT) {
        const lead = b.tone === TONES.LEADING || a.tone === TONES.LEADING;
        Object.assign(edge, { traversed: true, lead, alpha: lit * EDGE.ALPHA_SCALE, width: edge.width + (lead ? EDGE.LEAD_WIDTH : 0) });
      }
    }
    out.push(edge);
  });
  return out;
}

function adjacencyOf(edges) {
  const adjacency = new Map();
  const link = (a, b) => { if (!adjacency.has(a)) adjacency.set(a, new Set()); adjacency.get(a).add(b); };
  edges.forEach((e) => { link(e.src, e.dst); link(e.dst, e.src); });
  return adjacency;
}

function summaryOf(nodes) {
  const all = [...nodes.values()];
  const causes = all.filter((n) => n.role === ROLES.CAUSE);
  const live = causes.filter((n) => n.pct != null && n.tone !== TONES.EMBER).sort((a, b) => b.pct - a.pct);
  const ruled = causes.filter((n) => n.tone === TONES.EMBER);
  return {
    causes: causes.length,
    lit: causes.filter(isCandidate).length,
    ruledOut: ruled.length,
    observed: all.filter((n) => n.observed).length,
    leading: live.slice(0, MAX_LIVE_CAUSES).map((n) => ({ nid: n.nid, label: n.label, pct: n.pct, status: n.status })),
    ruled: ruled.map((n) => ({ nid: n.nid, label: n.label })),
  };
}

export function lightsFromState(bundle, diagState) {
  const nodes = baseNodes(bundle);
  const edges = list(bundle?.graph?.edges);
  const diag = diagState && list(diagState.causes).length ? diagState : null;
  if (diag) {
    applyCauses(nodes, diag);
    applyObserved(nodes, diag);
    fadeDeadFacts(nodes, edges);
  }
  const stage = bundle ? stageOf(diag, nodes) : STAGES.NONE;
  const focus = focusOf(stage, nodes, diag);
  dimOthersWhenLikely(stage, nodes);
  const clusterMass = new Map(list(diag?.lods).map((l) => [l.id, clamp01(Number(l.mass) || 0)]));
  const lightEdges = allEdges(nodes, edges, diag);
  return {
    nodes, edges: lightEdges, edgeById: new Map(lightEdges.map((e) => [e.id, e])), adjacency: adjacencyOf(lightEdges),
    focus, stage, clusterMass, summary: summaryOf(nodes),
  };
}

/** Whether a node is drawn under the view options (facts off hides unobserved facts; embers off hides ruled-out causes). */
export function isNodeShown(node, options) {
  if (!options) return true;
  if (!options.embers && node.tone === TONES.EMBER) return false;
  if (!options.facts && node.role === ROLES.FACT && !node.observed) return false;
  return true;
}

/** The lit nodes in a stable order for keyboard stepping: causes by % (leading first), then observed symptoms and facts. */
export function litOrder(lights) {
  const all = [...lights.nodes.values()];
  const rank = (n) => (n.tone === TONES.LEADING ? 2 : 0) + (n.role === ROLES.CAUSE ? 1 : 0);
  return all.filter((n) => isCandidate(n) || isObservedLit(n))
    .sort((a, b) => (rank(b) - rank(a)) || ((b.pct || 0) - (a.pct || 0)) || a.label.localeCompare(b.label))
    .map((n) => n.nid);
}
